
import React, { useState, useEffect } from 'react';
import PilotPage from './components/PilotPage';
import StayLocalPage from './components/StayLocalPage';
import CheckoutPage from './components/CheckoutPage';
import CleroConfirmed from './components/CleroConfirmed';
import CleroSession from './components/CleroSession';
import FriendPortal from './components/FriendPortal';
import CleroModal from './components/CleroModal';
import Walkthrough from './components/Walkthrough';
import PayerPage from './components/PayerPage';
import LandingPage from './components/LandingPage';
import PrivacyPage from './components/PrivacyPage';
import TermsPage from './components/TermsPage';
import DataDeletionPage from './components/DataDeletionPage';
import PlaidOAuthReturnPage from './components/PlaidOAuthReturnPage';
import GmailOAuthReturnPage from './components/GmailOAuthReturnPage';
import OnboardingReturnPage from './components/OnboardingReturnPage';
import AstraPayerReturnPage from './components/AstraPayerReturnPage';
import InvitePage from './components/InvitePage';
import GroupInvitePage from './components/GroupInvitePage';
import DirectPayClaimPage from './components/DirectPayClaimPage';
import UserProfileLandingPage from './components/UserProfileLandingPage';
import BusinessStorefrontPage from './components/business/BusinessStorefrontPage';
import BusinessPasscodeGate from './components/BusinessPasscodeGate';
import AdminDashboardPage from './components/AdminDashboardPage';
import FounderImpersonationBanner from './components/FounderImpersonationBanner';
import CompareSplitwisePage from './components/compare/CompareSplitwisePage';
import CompareVenmoPage from './components/compare/CompareVenmoPage';
import CompareZellePage from './components/compare/CompareZellePage';
import CompareHubPage from './components/compare/CompareHubPage';
import SupportPage from './components/SupportPage';
import BlogPage from './components/blog/BlogPage';
import { AppView, BookingDetails, SplitRequest, FriendStatus } from './types';
import { setSentryRoute } from './services/sentry';
import { trackWebAttributionEvent } from './services/attribution';
import { auth, founderAdminAuth, founderAdminFunctions, firebaseProjectId, isUsingFirebaseEmulator } from './services/firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import {
  WebImpersonationSession,
  readWebImpersonationSession,
  clearWebImpersonationSession,
  clearPublicImpersonationCaches,
  getWebImpersonationEventName,
} from './services/webImpersonation';

const waitForFounderAdminUser = async () => {
  if (founderAdminAuth.currentUser) {
    return founderAdminAuth.currentUser;
  }

  return new Promise<ReturnType<typeof Object> | null>((resolve) => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      unsubscribe();
      resolve(founderAdminAuth.currentUser);
    }, 1500);

    const unsubscribe = onAuthStateChanged(founderAdminAuth, (user) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      unsubscribe();
      resolve(user);
    });
  });
};

const INITIAL_BOOKING: BookingDetails = {
  title: "Oceanfront Villa with Infinity Pool",
  location: "Malibu, California",
  pricePerNight: 450,
  nights: 5,
  cleaningFee: 150,
  serviceFee: 89,
  total: 2489,
  dates: "Mar 15 – 20, 2024"
};

const isLocalhost = typeof window !== 'undefined' && Boolean(
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.startsWith('192.168.') ||
    window.location.hostname.endsWith('.local')
);

const appConsole = isLocalhost
    ? console
    : { log: () => {}, warn: () => {}, error: () => {}, debug: () => {} };

// Patch pushState to trigger popstate event for custom routing
const originalPushState = window.history.pushState;
window.history.pushState = function (data, title, url) {
  appConsole.log('[History] pushState:', { data, title, url });
  originalPushState.apply(window.history, [data, title, url]);
  window.dispatchEvent(new Event('popstate'));
};

const originalReplaceState = window.history.replaceState;
window.history.replaceState = function (data, title, url) {
  appConsole.log('[History] replaceState:', { data, title, url });
  originalReplaceState.apply(window.history, [data, title, url]);
  window.dispatchEvent(new Event('popstate'));
};

const App: React.FC = () => {
  const [route, setRoute] = useState<string>(window.location.pathname + window.location.search);
  const [activeRequest, setActiveRequest] = useState<SplitRequest | null>(null);
  const [isSetupOpen, setIsSetupOpen] = useState(false);
  const [impersonationSession, setImpersonationSession] = useState<WebImpersonationSession | null>(() => readWebImpersonationSession());
  const [isEndingImpersonation, setIsEndingImpersonation] = useState(false);
  const [impersonationExitError, setImpersonationExitError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search || '');
    const encodedDeepLink = String(params.get('deep_link') || '').trim();
    if (!encodedDeepLink) return;

    let decodedDeepLink = encodedDeepLink;
    try {
      decodedDeepLink = decodeURIComponent(encodedDeepLink);
    } catch (_error) {
      decodedDeepLink = encodedDeepLink;
    }

    if (!decodedDeepLink.startsWith('clero://')) return;

    const attemptKey = `deep_link_open_attempted:${decodedDeepLink}`;
    if (window.sessionStorage.getItem(attemptKey) === '1') return;
    window.sessionStorage.setItem(attemptKey, '1');

    const openDeepLink = () => {
      try {
        window.location.replace(decodedDeepLink);
      } catch (_error) {
        window.location.href = decodedDeepLink;
      }
    };

    const timer = window.setTimeout(openDeepLink, 40);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setRoute(window.location.pathname + window.location.search);
      window.scrollTo(0, 0);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const syncImpersonationState = () => {
      setImpersonationSession(readWebImpersonationSession());
    };

    syncImpersonationState();
    window.addEventListener(getWebImpersonationEventName(), syncImpersonationState as EventListener);
    return () => {
      window.removeEventListener(getWebImpersonationEventName(), syncImpersonationState as EventListener);
    };
  }, []);

  // Broadcast path to extension parent and ensure page scrolls to top
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    const pathname = window.location.pathname || '/';
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({
        type: 'CLERO_PATH_CHANGE',
        path: pathname,
        fullPath: pathname,
      }, '*');
    }
    setSentryRoute(pathname);
    void trackWebAttributionEvent();
  }, [route]);

  const navigate = (path: string) => {
    appConsole.log(`[App] navigate called with: ${path}`);
    window.history.pushState({}, '', path);
    // Include search if it was part of the original push but omitted from the path arg
    setRoute(window.location.pathname + window.location.search);
    window.scrollTo(0, 0);
  };

  const handleImpersonationStarted = (session: WebImpersonationSession) => {
    setImpersonationExitError('');
    setImpersonationSession(session);
  };

  const handleExitImpersonation = async () => {
    if (!impersonationSession || isEndingImpersonation) return;

    setIsEndingImpersonation(true);
    setImpersonationExitError('');
    try {
      const founderUser = await waitForFounderAdminUser();
      if (!founderUser) {
        throw new Error('Founder session is not ready yet. Please try again in a moment.');
      }
      const endImpersonation = httpsCallable<{ sessionId: string }, { sessionId: string; founderRestoreToken: string }>(
        founderAdminFunctions,
        'endImpersonationSession'
      );
      await endImpersonation({ sessionId: impersonationSession.sessionId });
      await signOut(auth);
      clearPublicImpersonationCaches();
      clearWebImpersonationSession();
      setImpersonationSession(null);
      navigate('/admin');
    } catch (error: any) {
      setImpersonationExitError(error?.message || 'Failed to exit impersonation. Please try again.');
    } finally {
      setIsEndingImpersonation(false);
    }
  };

  // Simple route matcher
  const getRouteInfo = () => {
    const rawPath = route.split('?')[0] || '/';
    const params = new URLSearchParams(route.split('?')[1] || window.location.search || '');

    // Aggressive normalization
    let path = rawPath.toLowerCase().trim();
    // Replace multiple slashes with single
    path = path.replace(/\/+/g, '/');
    // Remove trailing slash if length > 1
    if (path.length > 1 && path.endsWith('/')) {
      path = path.slice(0, -1);
    }
    // Ensure leading slash
    if (!path.startsWith('/')) {
      path = '/' + path;
    }

    appConsole.log(`[Router] Raw: "${rawPath}" -> Clean: "${path}" | Params: ${params.toString().substring(0, 50)}...`);

    if (path === '/demo') return { view: 'listing' as AppView };

    // Standard session bootstrap (e.g. from extension)
    const isExtensionBoot = params.get('src') === 'ext';
    const isNewSessionPath = path === '/s/new' || path === '/session/new' || path === '/new-session';

    if (isNewSessionPath || (path === '/' && isExtensionBoot) || (path === '/index.html' && isExtensionBoot)) {
      appConsole.log(`[Router] Matched: session-new! (Path: ${path}, Ext: ${isExtensionBoot})`);
      return { view: 'session-new' as AppView, params };
    }

    if (path.startsWith('/s/')) {
      // Extract from rawPath to preserve casing
      const id = rawPath.substring(rawPath.toLowerCase().indexOf('/s/') + 3);
      if (id && id.toLowerCase() !== 'new') {
        appConsole.log(`[Router] Matched legacy session link (id: ${id}), redirecting to home.`);
        window.history.replaceState({}, '', '/');
        return { view: 'landing' as AppView };
      }
    }

    if (path.startsWith('/o/')) {
      // Direct Organizer Access
      const id = rawPath.substring(rawPath.toLowerCase().indexOf('/o/') + 3);
      if (id) {
        appConsole.log(`[Router] Matched: organizer-session (id: ${id})`);
        return { view: 'organizer-session' as AppView, id };
      }
    }

    if (path.startsWith('/split/')) {
      const token = rawPath.substring(rawPath.toLowerCase().indexOf('/split/') + 7);
      if (token) {
        window.history.replaceState({}, '', `/pay/${token}${window.location.search || ''}`);
        appConsole.log(`[Router] Redirected /split/${token} to /pay/${token}`);
        return { view: 'payer-view' as AppView, token };
      }
    }

    if (path.startsWith('/pay/')) {
      const token = rawPath.substring(rawPath.toLowerCase().indexOf('/pay/') + 5);
      appConsole.log(`[Router] Matched: payer-view (token: ${token})`);
      return { view: 'payer-view' as AppView, token };
    }

    if (path.startsWith('/group-invite/')) {
      const token = rawPath.substring(rawPath.toLowerCase().indexOf('/group-invite/') + 14);
      if (token) {
        return { view: 'group-invite' as AppView, token };
      }
    }

    if (path.startsWith('/claim/')) {
      const token = rawPath.substring(rawPath.toLowerCase().indexOf('/claim/') + 7);
      if (token) {
        appConsole.log(`[Router] Matched: claim-direct-pay (token: ${token})`);
        return { view: 'claim-direct-pay' as AppView, token };
      }
    }

    if (path.startsWith('/u/')) {
      const rawUsername = rawPath.substring(rawPath.toLowerCase().indexOf('/u/') + 3).trim();
      const username = rawUsername.replace(/^@+/, '').split('?')[0].split('/')[0];
      if (username) {
        appConsole.log(`[Router] Matched: user-profile (username: ${username})`);
        return { view: 'user-profile' as AppView, username };
      }
    }

    if (path.startsWith('/t/')) {
      const rawCode = rawPath.substring(rawPath.toLowerCase().indexOf('/t/') + 3).trim();
      const code = rawCode.split('?')[0].split('/')[0];
      if (code) {
        appConsole.log(`[Router] Matched merch QR code: ${code}`);
        const redirectUrl = isUsingFirebaseEmulator
          ? `http://${window.location.hostname}:5001/${firebaseProjectId}/us-central1/resolveMerchQr?code=${encodeURIComponent(code)}`
          : `https://us-central1-${firebaseProjectId}.cloudfunctions.net/resolveMerchQr?code=${encodeURIComponent(code)}`;
        window.location.replace(redirectUrl);
        return { view: 'landing' as AppView };
      }
    }

    if (path.startsWith('/b/')) {
      const rawUsername = rawPath.substring(rawPath.toLowerCase().indexOf('/b/') + 3).trim();
      const username = rawUsername.replace(/^@+/, '').split('?')[0].split('/')[0];
      if (username) {
        appConsole.log(`[Router] Matched: business-profile (username: ${username})`);
        return { view: 'business-profile' as AppView, username };
      }
    }

    if (path.startsWith('/return/')) {
      const sessionId = rawPath.substring(rawPath.toLowerCase().indexOf('/return/') + 8);
      appConsole.log(`[Router] Matched: payout-return (sessionId: ${sessionId})`);
      return { view: 'payout-return' as AppView, sessionId };
    }

    if (path === '/checkout') return { view: 'checkout' as AppView };
    if (path === '/clero-confirmed') return { view: 'clero-confirmed' as AppView };
    if (path === '/clero-session') return { view: 'clero-session' as AppView };
    if (path === '/pilot') return { view: 'pilot' as AppView };
    if (path === '/privacy') return { view: 'privacy' as AppView };
    if (path === '/terms') return { view: 'terms' as AppView };
    if (path === '/data-deletion') return { view: 'data-deletion' as AppView };
    if (path === '/find' || path === '/find-my-clero') return { view: 'find-my-clero' as AppView };
    if (path === '/invite') return { view: 'invite' as AppView };
    if (path === '/admin' || path.startsWith('/admin/')) return { view: 'admin' as AppView };
    if (path === '/plaid-oauth') return { view: 'plaid-oauth' as AppView };
    if (path === '/gmail-oauth') return { view: 'gmail-oauth' as AppView };
    if (path === '/payer-astra-return') return { view: 'payer-astra-return' as AppView };
    if (path === '/onboarding/complete') return { view: 'onboarding-complete' as AppView };
    if (path === '/onboarding/refresh') return { view: 'onboarding-refresh' as AppView };
    if (path === '/compare' || path === '/vs' || path === '/compare/apps' || path === '/compare/all' || path === '/compare/splitting-apps' || path === '/compare/split' || path === '/compare/splitit') return { view: 'compare' as AppView };
    if (path === '/compare/splitwise' || path === '/vs/splitwise' || path === '/vs-splitwise') return { view: 'compare-splitwise' as AppView };
    if (path === '/compare/venmo' || path === '/vs/venmo' || path === '/vs-venmo') return { view: 'compare-venmo' as AppView };
    if (path === '/compare/zelle' || path === '/vs/zelle' || path === '/vs-zelle') return { view: 'compare-zelle' as AppView };
    if (path === '/support' || path === '/help' || path === '/contact') return { view: 'support' as AppView };
    if (path.startsWith('/blog')) return { view: 'blog' as AppView };

    // Default to landing
    appConsole.log(`[Router] No match for "${path}". Defaulting to landing.`);
    return { view: 'landing' as AppView };
  };

  const { view, params, id, token, sessionId, username } = getRouteInfo() as any;

  const handleCheckoutComplete = (isSplitEnabled: boolean) => {
    if (isSplitEnabled) {
      setIsSetupOpen(true);
    } else {
      navigate('/');
      alert("Payment successful! (Clero was disabled)");
    }
  };

  const handleSetupComplete = (friends: FriendStatus[], organizerName: string) => {
    setIsSetupOpen(false);
    const req: SplitRequest = {
      id: "CLR-" + Math.random().toString(36).substr(2, 5).toUpperCase(),
      requesterName: organizerName || "Alex",
      amountPerPerson: INITIAL_BOOKING.total / (friends.length + 1),
      totalAmount: INITIAL_BOOKING.total,
      bookingTitle: INITIAL_BOOKING.title,
      flow: 'pay_now',
      friends: friends,
      portalType: 'repayment'
    };
    setActiveRequest(req);
    navigate('/clero-confirmed');
  };

  const appShellBackground = view === 'session-gateway' ? 'bg-transparent' : 'bg-white';

  return (
    <div className={`min-h-screen ${appShellBackground} text-black selection:bg-black selection:text-white`}>
      {impersonationSession && view !== 'admin' ? (
        <FounderImpersonationBanner
          session={impersonationSession}
          onExit={() => {
            void handleExitImpersonation();
          }}
          isExiting={isEndingImpersonation}
          error={impersonationExitError}
        />
      ) : null}

      {view === 'landing' && (
        <LandingPage onStartDemo={() => navigate('/demo')} />
      )}

      {view === 'pilot' && (
        <PilotPage onStartDemo={() => navigate('/demo')} />
      )}

      {view === 'listing' && (
        <>
          <StayLocalPage
            booking={INITIAL_BOOKING}
            onReserve={() => navigate('/checkout')}
            onBackToPilot={() => navigate('/pilot')}
          />
          <Walkthrough
            step="1"
            instruction="Imagine you are checking out. You've found a high-value purchase. Click 'Book Stay' (our example purchase) to proceed."
          />
        </>
      )}

      {view === 'checkout' && (
        <>
          <CheckoutPage
            booking={INITIAL_BOOKING}
            onBack={() => navigate('/')}
            onComplete={handleCheckoutComplete}
          />
          {!isSetupOpen && (
            <Walkthrough
              step="2"
              instruction="Click on 'Split Now' in top right corner to enable Clero and open the Clero session."
            />
          )}
        </>
      )}

      {view === 'session-new' && (
        <div className="min-h-screen flex items-center justify-center bg-white p-6 text-center">
          <div className="max-w-md space-y-3">
            <h1 className="text-xl font-semibold">Create splits in Clero app</h1>
            <p className="text-sm text-gray-600">Web does not create or manage split sessions. Use Clero mobile app to start a split.</p>
          </div>
        </div>
      )}


      {view === 'organizer-session' && (
        <div className="min-h-screen flex items-center justify-center bg-white p-6 text-center">
          <div className="max-w-md space-y-3">
            <h1 className="text-xl font-semibold">Organizer tools are mobile-only</h1>
            <p className="text-sm text-gray-600">Use Clero app to manage this split.</p>
          </div>
        </div>
      )}

      {view === 'payer-view' && (
        <PayerPage token={token!} />
      )}

      {view === 'payout-return' && (
        <div className="min-h-screen flex items-center justify-center bg-white p-6 text-center">
          <div className="max-w-md space-y-3">
            <h1 className="text-xl font-semibold">Continue in Clero app</h1>
            <p className="text-sm text-gray-600">Payout setup and split management are available in the Clero mobile app.</p>
          </div>
        </div>
      )}

      {view === 'privacy' && <PrivacyPage />}
      {view === 'terms' && <TermsPage />}
      {view === 'data-deletion' && <DataDeletionPage />}
      {view === 'find-my-clero' && (
        <div className="min-h-screen flex items-center justify-center bg-white p-6 text-center">
          <div className="max-w-md space-y-3">
            <h1 className="text-xl font-semibold">Continue in Clero app</h1>
            <p className="text-sm text-gray-600">Split history and organizer tools are available in the Clero mobile app.</p>
          </div>
        </div>
      )}
      {view === 'invite' && <InvitePage />}
      {view === 'admin' && <AdminDashboardPage onImpersonationStarted={handleImpersonationStarted} />}
      {view === 'group-invite' && <GroupInvitePage token={token!} />}
      {view === 'claim-direct-pay' && <DirectPayClaimPage token={token!} />}
      {view === 'user-profile' && <UserProfileLandingPage username={username!} />}
      {view === 'business-profile' && (
        <BusinessPasscodeGate username={username!}>
          <BusinessStorefrontPage username={username!} />
        </BusinessPasscodeGate>
      )}
      {view === 'plaid-oauth' && <PlaidOAuthReturnPage />}
      {view === 'gmail-oauth' && <GmailOAuthReturnPage />}
      {view === 'payer-astra-return' && <AstraPayerReturnPage />}
      {view === 'onboarding-complete' && <OnboardingReturnPage type="complete" />}
      {view === 'onboarding-refresh' && <OnboardingReturnPage type="refresh" />}
      {view === 'compare' && <CompareHubPage />}
      {view === 'compare-splitwise' && <CompareSplitwisePage />}
      {view === 'compare-venmo' && <CompareVenmoPage />}
      {view === 'compare-zelle' && <CompareZellePage />}
      {view === 'support' && <SupportPage />}
      {view === 'blog' && <BlogPage currentPath={route} />}

      {view === 'clero-confirmed' && activeRequest && (
        <>
          <CleroConfirmed
            request={activeRequest}
            onGoToSession={() => navigate('/clero-session')}
            onBackToHome={() => navigate('/')}
          />
          <Walkthrough
            step="4"
            instruction="Notice we confirmed the order on the store page first. Now, Clero is active. Click 'Track progress'."
          />
        </>
      )}

      {view === 'clero-session' && activeRequest && (
        <>
          <CleroSession
            request={activeRequest}
            booking={INITIAL_BOOKING}
            onSimulatePaid={(friendId) => {
              const updatedFriends = activeRequest.friends.map(f =>
                f.id === friendId ? { ...f, status: 'paid' as const } : f
              );
              setActiveRequest({ ...activeRequest, friends: updatedFriends });
            }}
            onUpdateShare={(friendId, newAmount) => {
              const updatedFriends = activeRequest.friends.map(f =>
                f.id === friendId ? { ...f, amount: newAmount } : f
              );
              setActiveRequest({ ...activeRequest, friends: updatedFriends });
            }}
            onFriendView={() => navigate('/friend-portal')}
            onBackToHome={() => navigate('/')}
          />
          <Walkthrough
            step="5"
            instruction="This is your dashboard. You can see who hasn't paid yet. Click on any friend to simulate them paying back their share."
          />
        </>
      )}

      {isSetupOpen && (
        <CleroModal
          isOpen={isSetupOpen}
          flow="pay_now"
          onClose={() => {
            setIsSetupOpen(false);
            navigate('/');
          }}
          booking={INITIAL_BOOKING}
          onComplete={handleSetupComplete}
        />
      )}

      {/* Persistent global reCAPTCHA containers to prevent timing errors during page unmounting */}
      <div id="recaptcha-container" className="fixed bottom-2 right-2 z-50 pointer-events-none" />
      <div id="session-gateway-recaptcha" className="fixed bottom-2 right-2 z-50 pointer-events-none" />
      <div id="organizer-session-recaptcha" className="fixed bottom-2 right-2 z-50 pointer-events-none" />
      <div id="find-my-clero-recaptcha" className="fixed bottom-2 right-2 z-50 pointer-events-none" />
      <div id="admin-dashboard-recaptcha" className="fixed bottom-2 right-2 z-50 pointer-events-none" />
    </div>
  );
};

export default App;
